'use client';

import Image from 'next/image';
import { useT } from '@/lib/i18n';

/**
 * The visible site logotype: rounded icon + wordmark + tagline.
 *
 * Renders the icon directly via `next/image` (no absolute-positioned
 * gradient fallback — that earlier construction was tripping Lighthouse's
 * "Image elements have explicit width and height" / aspect-ratio audit
 * because the inner `<Image>` was sized via CSS w-full/h-full while also
 * carrying explicit width/height attrs).
 *
 * The 192×192 source is auto-served as AVIF/WebP at exactly the rendered
 * size by Vercel's image optimizer (sizes attribute pinned to `dim` px),
 * with a 30-day browser cache (next.config images.minimumCacheTTL).
 */
export function Brand({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const t = useT();
  const dim = size === 'sm' ? 36 : size === 'lg' ? 64 : 44;
  const titleClass =
    size === 'sm'
      ? 'text-xl'
      : size === 'lg'
        ? 'text-3xl sm:text-4xl'
        : 'text-2xl sm:text-3xl';

  return (
    <div className="flex items-center gap-3">
      <Image
        src="/logo-fangass-icon-192.png"
        alt="FanGass"
        width={dim}
        height={dim}
        priority
        sizes={`${dim}px`}
        className="rounded-full shadow-sm shrink-0"
      />
      <div className="leading-tight">
        <div className={`font-heading font-extrabold tracking-tight ${titleClass}`}>
          <span style={{ color: 'var(--color-ocean)' }}>fan</span>
          <span style={{ color: 'var(--color-turquoise)' }}>gass</span>
        </div>
        <div className="text-[11px] sm:text-xs text-muted-foreground -mt-0.5">
          {t('brand_tagline')}
        </div>
      </div>
    </div>
  );
}
